import { differenceInDays } from '@if/core/date'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const getColorForDifference = (valueBefore: number, valueAfter: number) => {
  if (valueAfter > valueBefore) return 'error'
  if (valueAfter < valueBefore) return 'success'
  return 'inherit'
}

export const useLoanCalendarActionPreviewData = (
  simulationBefore: CalendarSimulation,
  simulationAfter: CalendarSimulation,
  actionDate?: Date,
) => {
  const { t } = useTranslation()
  const lastPaymentDateBefore = useMemo(() => simulationBefore.getLastPaymentDate(), [simulationBefore])
  const lastPaymentDateAfter = useMemo(() => simulationAfter.getLastPaymentDate(), [simulationAfter])
  const lastPaymentDateDifference = useMemo(() => {
    const diff = differenceInDays(lastPaymentDateAfter, lastPaymentDateBefore)
    if (diff > 0) return `+ ${t('common.days', { count: diff })}`
    if (diff < 0) return `- ${t('common.days', { count: -diff })}`
    return null
  }, [lastPaymentDateAfter, lastPaymentDateBefore, t])

  const lastTotalInterestPaidBefore = useMemo(() => {
    return simulationBefore.operationInformations.reduce(
      (total, opInfo) => total + opInfo.operationResultDto.interestPaid,
      0,
    )
  }, [simulationBefore.operationInformations])

  const lastTotalInterestPaidAfter = useMemo(() => {
    return simulationAfter.operationInformations.reduce(
      (total, opInfo) => total + opInfo.operationResultDto.interestPaid,
      0,
    )
  }, [simulationAfter.operationInformations])

  const selectedBeforeOperation = useMemo(
    () =>
      actionDate && simulationBefore.operationInformations.findLast((oi) => oi.operation.effectiveDate <= actionDate),
    [actionDate, simulationBefore.operationInformations],
  )
  const selectedAfterOperation = useMemo(
    () =>
      actionDate && simulationAfter.operationInformations.findLast((oi) => oi.operation.effectiveDate <= actionDate),
    [actionDate, simulationAfter.operationInformations],
  )

  const totalInterestPaidDifference = lastTotalInterestPaidAfter - lastTotalInterestPaidBefore

  return useMemo(
    () => ({
      lastPaymentDateBefore,
      lastPaymentDateAfter,
      lastPaymentDateDifference,
      selectedBeforeOperation,
      selectedAfterOperation,
      lastTotalInterestPaidBefore,
      lastTotalInterestPaidAfter,
      totalInterestPaidDifference,
    }),
    [
      lastPaymentDateAfter,
      lastPaymentDateBefore,
      lastPaymentDateDifference,
      lastTotalInterestPaidAfter,
      lastTotalInterestPaidBefore,
      selectedAfterOperation,
      selectedBeforeOperation,
      totalInterestPaidDifference,
    ],
  )
}
